import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';

import { useAuthStore } from '@/stores/auth';
import { isHiddenRoute } from '@/config';

declare module 'vue-router' {
    interface RouteMeta {
        title?: string;
        requiresAuth?: boolean;
        guestOnly?: boolean;
        requiredRole?: string | string[];
        /** Users and its reports: Super Admin plus the `View Users` permission. */
        requiresUserAccess?: boolean;
    }
}

/**
 * Does a user holding `roles` satisfy a route's `requiredRole`?
 *
 * A route may name one role or several; several means any one of them is
 * enough. Exported because the alternative is proving this through a real
 * navigation, which lazy-loads page components and turns a permission test
 * into an integration test of whatever that page happens to fetch.
 */
export function satisfiesRequiredRole(
    required: string | string[] | undefined,
    hasRole: (role: string) => boolean,
): boolean {
    if (!required) {
        return true;
    }

    return Array.isArray(required) ? required.some((role) => hasRole(role)) : hasRole(required);
}

const routes: RouteRecordRaw[] = [
    {
        path: '/login',
        name: 'login',
        component: () => import('@/pages/auth/Login.vue'),
        meta: { guestOnly: true, title: 'Sign in' },
    },
    {
        path: '/',
        component: () => import('@/layouts/AppLayout.vue'),
        // Either role reaches the dashboard. The API decides what each can
        // actually do — Super Admin keeps users, roles, tenants, migrations,
        // transitions and the irreversible curriculum edits — but a Content
        // Editor must get past the router first. Gating the shell on Super
        // Admin alone bounced them back to the login screen, which reads as a
        // broken sign-in rather than a permissions boundary.
        meta: { requiresAuth: true, requiredRole: ['Super Admin', 'Content Editor'] },
        children: [
            {
                path: '',
                redirect: { name: 'dashboard' },
            },
            {
                // The admin guide, rendered from docs/admin. No requiredRole:
                // help is the last thing that should be gated, and the pages
                // are static prose with nothing to authorise.
                path: 'help',
                name: 'help',
                component: () => import('@/pages/help/Guide.vue'),
                meta: { title: 'Guide' },
            },
            {
                path: 'help/:slug',
                name: 'help.show',
                component: () => import('@/pages/help/Guide.vue'),
                props: true,
                meta: { title: 'Guide' },
            },
            {
                path: 'dashboard',
                name: 'dashboard',
                component: () => import('@/pages/Dashboard.vue'),
                meta: { title: 'Dashboard' },
            },
            {
                // Resolves the `glossary`-slug Page by id and redirects to
                // pages.show — see pages/Glossary.vue.
                path: 'glossary',
                name: 'glossary',
                component: () => import('@/pages/Glossary.vue'),
                meta: { title: 'Glossary' },
            },
            {
                path: 'jurisdictions',
                name: 'jurisdictions.index',
                component: () => import('@/pages/jurisdictions/JurisdictionsIndex.vue'),
                meta: { title: 'Jurisdictions' },
            },
            {
                path: 'jurisdictions/:id(\\d+)',
                name: 'jurisdictions.show',
                component: () => import('@/pages/jurisdictions/JurisdictionShow.vue'),
                meta: { title: 'Jurisdiction' },
            },
            {
                path: 'frameworks',
                name: 'frameworks.index',
                component: () => import('@/pages/frameworks/FrameworksIndex.vue'),
                meta: { title: 'Frameworks' },
            },
            {
                path: 'frameworks/:id(\\d+)',
                name: 'frameworks.show',
                component: () => import('@/pages/frameworks/FrameworkShow.vue'),
                meta: { title: 'Framework' },
            },
            {
                path: 'standards',
                name: 'standards.index',
                component: () => import('@/pages/standards/StandardsIndex.vue'),
                meta: { title: 'Standards' },
            },
            {
                path: 'standards/crosswalk-import',
                name: 'standards.crosswalk-import',
                component: () => import('@/pages/standards/CrosswalkImport.vue'),
                meta: { title: 'Import a crosswalk' },
            },
            {
                path: 'standards/import',
                name: 'standards.import',
                component: () => import('@/pages/standards/StandardImport.vue'),
                meta: { title: 'Import standards' },
            },
            {
                path: 'standards/:id(\\d+)',
                name: 'standards.show',
                component: () => import('@/pages/standards/StandardShow.vue'),
                meta: { title: 'Standard' },
            },
            {
                path: 'mappings',
                name: 'mappings.index',
                component: () => import('@/pages/mappings/MappingsIndex.vue'),
                meta: { title: 'Mappings' },
            },
            {
                path: 'mappings/import',
                name: 'mappings.import',
                component: () => import('@/pages/mappings/MappingImport.vue'),
                meta: { title: 'Import mappings' },
            },
            {
                path: 'mappings/:id(\\d+)',
                name: 'mappings.show',
                component: () => import('@/pages/mappings/MappingShow.vue'),
                meta: { title: 'Mapping' },
            },
            {
                path: 'transitions',
                name: 'transitions.index',
                component: () => import('@/pages/transitions/TransitionsIndex.vue'),
                meta: { title: 'Transitions' },
            },
            {
                path: 'transitions/:id(\\d+)',
                name: 'transitions.show',
                component: () => import('@/pages/transitions/TransitionShow.vue'),
                meta: { title: 'Transition' },
            },
            {
                path: 'migrations',
                name: 'migrations.index',
                component: () => import('@/pages/migrations/MigrationsIndex.vue'),
                meta: { title: 'Framework migrations' },
            },
            {
                path: 'migrations/:id(\\d+)',
                name: 'migrations.show',
                component: () => import('@/pages/migrations/MigrationShow.vue'),
                meta: { title: 'Framework migration' },
            },
            {
                path: 'authors',
                name: 'authors.index',
                component: () => import('@/pages/authors/AuthorsIndex.vue'),
                meta: { title: 'Authors' },
            },
            {
                path: 'providers',
                name: 'providers.index',
                component: () => import('@/pages/providers/ProvidersIndex.vue'),
                meta: { title: 'Providers' },
            },
            {
                path: 'learning-targets',
                name: 'learningTargets.index',
                component: () => import('@/pages/learningTargets/LearningTargetsIndex.vue'),
                meta: { title: 'Learning targets' },
            },
            {
                path: 'content',
                name: 'content.index',
                component: () => import('@/pages/content/ContentIndex.vue'),
                meta: { title: 'Content' },
            },
            {
                path: 'content/new',
                name: 'content.create',
                component: () => import('@/pages/content/ContentCreate.vue'),
                meta: { title: 'New content' },
            },
            {
                path: 'content/import',
                name: 'content.import',
                component: () => import('@/pages/content/ContentImport.vue'),
                meta: { title: 'Import content' },
            },
            {
                path: 'content/:id(\\d+)',
                name: 'content.show',
                component: () => import('@/pages/content/ContentShow.vue'),
                meta: { title: 'Content' },
            },
            {
                path: 'users',
                name: 'users.index',
                component: () => import('@/pages/users/UsersIndex.vue'),
                meta: { title: 'Users', requiresUserAccess: true },
            },
            {
                path: 'users/labels',
                name: 'user-labels.index',
                component: () => import('@/pages/users/UserLabelsIndex.vue'),
                meta: { title: 'User labels', requiresUserAccess: true },
            },
            {
                path: 'users/:id(\\d+)',
                name: 'users.show',
                component: () => import('@/pages/users/UserShow.vue'),
                meta: { title: 'User', requiresUserAccess: true },
            },
            {
                path: 'roles',
                name: 'roles.index',
                component: () => import('@/pages/roles/RolesIndex.vue'),
                meta: { title: 'Roles & permissions' },
            },
            {
                path: 'pages',
                name: 'pages.index',
                component: () => import('@/pages/pages/PagesIndex.vue'),
                meta: { title: 'Pages' },
            },
            {
                path: 'pages/:id(\\d+)',
                name: 'pages.show',
                component: () => import('@/pages/pages/PageShow.vue'),
                meta: { title: 'Page' },
            },
            {
                path: 'localizations',
                name: 'localizations.index',
                component: () => import('@/pages/localizations/LocalizationsIndex.vue'),
                meta: { title: 'AI localization' },
            },
            {
                path: 'localizations/:id(\\d+)',
                name: 'localizations.show',
                component: () => import('@/pages/localizations/LocalizationShow.vue'),
                meta: { title: 'Localized lesson' },
            },
            {
                path: 'localization-feedback',
                name: 'localization-feedback.index',
                component: () => import('@/pages/localizations/FeedbackIndex.vue'),
                meta: { title: 'AI localization feedback' },
            },
            {
                path: 'localization-feedback/:id(\\d+)',
                name: 'localization-feedback.show',
                component: () => import('@/pages/localizations/FeedbackShow.vue'),
                meta: { title: 'Feedback' },
            },
            {
                path: 'press-media',
                name: 'press-media.index',
                component: () => import('@/pages/pressMedia/PressMediaIndex.vue'),
                meta: { title: 'Press and media' },
            },
            {
                path: 'press-media/featured',
                name: 'press-media.featured',
                component: () => import('@/pages/pressMedia/FeaturedCarousel.vue'),
                meta: { title: 'Featured carousel' },
            },
            {
                path: 'press-media/:id(\\d+)',
                name: 'press-media.show',
                component: () => import('@/pages/pressMedia/PressMediaShow.vue'),
                meta: { title: 'Press and media item' },
            },
            {
                path: 'banners',
                name: 'banners.index',
                component: () => import('@/pages/banners/BannersIndex.vue'),
                meta: { title: 'Site banners' },
            },
            {
                path: 'banners/order',
                name: 'banners.order',
                component: () => import('@/pages/banners/BannerOrder.vue'),
                meta: { title: 'Banner order' },
            },
            {
                path: 'banners/:id(\\d+)',
                name: 'banners.show',
                component: () => import('@/pages/banners/BannerShow.vue'),
                meta: { title: 'Site banner' },
            },
            {
                path: 'faqs',
                name: 'faqs.index',
                component: () => import('@/pages/faqs/FaqsIndex.vue'),
                meta: { title: 'Lesson plans FAQs' },
            },
            {
                path: 'faqs/order',
                name: 'faqs.order',
                component: () => import('@/pages/faqs/FaqOrder.vue'),
                meta: { title: 'FAQ order' },
            },
            {
                path: 'faqs/:id(\\d+)',
                name: 'faqs.show',
                component: () => import('@/pages/faqs/FaqShow.vue'),
                meta: { title: 'FAQ' },
            },
            // The News list and detail screens are gone. They wrote to the
            // legacy `news` table, which the site stopped reading at the
            // cutover, so every save there looked like it worked and changed
            // nothing. News is edited under Content, and the export that used
            // to live on the list is on the content list now (#2864).
            //
            // /news and /news/{id} now resolve to nothing and fall through to
            // the catch-all, which is the honest answer: the screens do not
            // exist rather than existing and refusing to save.
            {
                path: 'news-categories',
                name: 'news-categories.index',
                component: () => import('@/pages/news/NewsCategories.vue'),
                meta: { title: 'News categories' },
            },
            {
                path: 'tenants',
                name: 'tenants.index',
                component: () => import('@/pages/tenants/TenantsIndex.vue'),
                meta: { title: 'Tenants' },
            },
            {
                path: 'tenants/:id(\\d+)',
                name: 'tenants.show',
                component: () => import('@/pages/tenants/TenantShow.vue'),
                meta: { title: 'Tenant' },
            },
        ],
    },
    {
        path: '/:pathMatch(.*)*',
        name: 'not-found',
        component: () => import('@/pages/NotFound.vue'),
    },
];

export const router = createRouter({
    history: createWebHistory(),
    routes,
});

router.beforeEach(async (to) => {
    const auth = useAuthStore();

    // Administration routes are hidden on staging during the content QA cycle —
    // keep them unreachable by direct URL too, not just absent from the nav.
    if (isHiddenRoute(to.name as string | null | undefined)) {
        return { name: 'dashboard' };
    }

    if (to.meta.guestOnly && auth.isAuthenticated) {
        return { name: 'dashboard' };
    }

    if (to.meta.requiresAuth && !auth.isAuthenticated) {
        return { name: 'login', query: { redirect: to.fullPath } };
    }

    if (!satisfiesRequiredRole(to.meta.requiredRole, (role) => auth.hasRole(role))) {
        // Signed in, but not somebody the dashboard admits. End the session so
        // they are not left half-logged-in (and bounced back here on the next
        // visit), and say why on the login screen — the same `not_staff` key
        // the Google sign-in callback uses.
        await auth.logout();
        return { name: 'login', query: { auth_error: 'not_staff' } };
    }

    // Staff who may use the dashboard but not Users stay signed in and land on
    // the dashboard, rather than on a page whose every request is refused.
    if (to.meta.requiresUserAccess && !auth.canSeeUsers) {
        return { name: 'dashboard' };
    }

    return true;
});

router.afterEach((to) => {
    const base = 'STC Admin';
    document.title = to.meta.title ? `${to.meta.title} · ${base}` : base;
});
